import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { StatCard } from '@thegpvc/ui';
import type { TaskQueueStatus } from '../types/api';
import { SectionCard } from './SectionCard';
import { formatInt, formatTimestamp } from '../utils/format';
import { text, cx } from '../styles/theme';

interface QueueMetricsProps {
  status: TaskQueueStatus;
}

export function QueueMetrics({ status }: QueueMetricsProps) {
  const { statusCounts, typeCounts, workers, avgProcessingTimeMs, recentFailures } = status;

  const totalTasks = statusCounts.pending + statusCounts.processing + statusCounts.completed + statusCounts.failed;
  const failureRate = totalTasks > 0 ? (statusCounts.failed / totalTasks) * 100 : 0;

  const avgProcessing = parseInt(avgProcessingTimeMs) > 0
    ? `${(parseInt(avgProcessingTimeMs) / 1000).toFixed(1)}s`
    : '—';

  return (
    <div className="space-y-6">
      {/* Status overview — transparent stat tiles nested in one card. */}
      <SectionCard title="Status">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            surface="plain"
            label="Pending"
            value={formatInt(statusCounts.pending)}
            icon={<Clock className="w-5 h-5 text-amber-500" />}
          />
          <StatCard
            surface="plain"
            label="Processing"
            value={formatInt(statusCounts.processing)}
            icon={<Loader2 className={cx('w-5 h-5 text-blue-500', statusCounts.processing > 0 && 'animate-spin')} />}
          />
          <StatCard
            surface="plain"
            label="Completed"
            value={formatInt(statusCounts.completed)}
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          />
          <StatCard
            surface="plain"
            label="Failed"
            value={formatInt(statusCounts.failed)}
            icon={<XCircle className="w-5 h-5 text-rose-500" />}
          />
        </div>
        {failureRate > 0 && (
          <p className={cx('text-xs', text.muted)}>{failureRate.toFixed(1)}% failure rate</p>
        )}
      </SectionCard>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SectionCard title="Task Types">
          <TaskTypeRow label="Link Session" count={typeCounts.linkSession} />
          <TaskTypeRow label="Extract Stats" count={typeCounts.extractStats} />
        </SectionCard>

        <SectionCard title="Workers">
          <div className={cx('text-3xl font-bold font-mono tabular-nums', text.primary)}>
            {status.activeWorkers}
          </div>
          {workers.length > 0 && (
            <div className="space-y-1 text-sm">
              {workers.slice(0, 3).map((worker) => (
                <div key={worker.workerId} className={text.secondary}>
                  <span className="font-mono text-xs">{worker.workerId.split('-')[0]}</span>:{' '}
                  {worker.activeTasks} active
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Performance">
          <div className={cx('text-3xl font-bold font-mono tabular-nums', text.primary)}>
            {avgProcessing}
          </div>
          <p className={cx('text-sm', text.secondary)}>Avg processing time</p>
        </SectionCard>
      </div>

      {/* Recent Failures */}
      {recentFailures.length > 0 && (
        <SectionCard
          title="Recent Failures"
          action={
            status.lastFailureAt && parseInt(status.lastFailureAt) > 0 ? (
              <span className={cx('text-xs', text.muted)}>
                Last: {formatTimestamp(status.lastFailureAt)}
              </span>
            ) : undefined
          }
        >
          {recentFailures.slice(0, 5).map((failure) => {
            const failedAtTimestamp = parseInt(failure.failedAt);
            const failedAtDate = failedAtTimestamp > 0 ? new Date(failedAtTimestamp * 1000) : null;

            return (
              <div
                key={failure.taskId}
                className="flex items-start gap-3 p-3 rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/10"
              >
                <XCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={cx('font-medium text-sm', text.primary)}>
                      {failure.taskType}
                    </span>
                    {failedAtDate && (
                      <span className={cx('text-xs', text.muted)} title={failedAtDate.toLocaleString()}>
                        {formatTimestamp(failure.failedAt)}
                      </span>
                    )}
                  </div>
                  <p className={cx('text-sm truncate', text.secondary)}>{failure.filePath}</p>
                  {failure.errorMessage && (
                    <p className="text-xs text-rose-700 dark:text-rose-400 mt-1 font-mono">
                      {failure.errorMessage}
                    </p>
                  )}
                  <div className={cx('flex items-center gap-2 mt-1 text-xs', text.muted)}>
                    <span>Attempts: {failure.attempts}</span>
                    {failure.projectName && <span>• {failure.projectName}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </SectionCard>
      )}
    </div>
  );
}

interface TaskTypeRowProps {
  label: string;
  count: number;
}

function TaskTypeRow({ label, count }: TaskTypeRowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className={cx('text-sm', text.secondary)}>{label}</span>
      <span className={cx('text-sm font-semibold font-mono tabular-nums', text.primary)}>
        {formatInt(count)}
      </span>
    </div>
  );
}
